import dayjs from 'dayjs';
import { toast } from 'react-hot-toast';

export const fmtDate = (d, f = 'DD MMM YYYY') => (d ? dayjs(d).format(f) : '—');
export const fmtTime = (d) => (d ? dayjs(d).format('hh:mm A') : '—');
export const fmtDateTime = (d) => (d ? dayjs(d).format('DD MMM, hh:mm A') : '—');

// Format an "HH:MM" 24h string into "h:mm AM/PM".
export const fmtTime12 = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};

// Hours between two "HH:MM" strings.
export const hoursBetween = (startTime, endTime) => {
  if (!startTime || !endTime) return 0;
  const [sh, sm] = startTime.split(':').map(Number);
  const [eh, em] = endTime.split(':').map(Number);
  if (!Number.isFinite(sh) || !Number.isFinite(sm) || !Number.isFinite(eh) || !Number.isFinite(em)) return 0;
  return Math.max(0, ((eh * 60 + em) - (sh * 60 + sm)) / 60);
};

export const minutesToHours = (m) => {
  if (!m && m !== 0) return '—';
  const h = Math.floor(m / 60);
  const min = m % 60;
  return `${h}h ${min}m`;
};

// Live working minutes for an in-progress day. Adds up every check-in → check-out
// session (an open session counts up to `now`), minus the lunch break, so the
// number the employee watches matches what the server saves at check-out.
export const liveWorkingMinutes = (att, now = Date.now()) => {
  if (!att?.checkInAt) return 0;
  const sessions =
    Array.isArray(att.sessions) && att.sessions.length
      ? att.sessions
      : [{ checkInAt: att.checkInAt, checkOutAt: att.checkOutAt }]; // legacy single-session record
  let total = 0;
  for (const s of sessions) {
    if (!s.checkInAt) continue;
    const end = s.checkOutAt ? new Date(s.checkOutAt).getTime() : now; // open session ticks to now
    total += (end - new Date(s.checkInAt).getTime()) / 60000;
  }
  let lunch = att.lunchMinutes || 0;
  // A lunch break in progress isn't in lunchMinutes yet — subtract it live.
  if (att.lunchStartAt && !att.lunchEndAt) {
    lunch += (now - new Date(att.lunchStartAt).getTime()) / 60000;
  }
  return Math.max(0, Math.round(total - lunch));
};

// Tracker durations come from the API in seconds.
export const secondsToHours = (s) => {
  if (!s && s !== 0) return '—';
  const total = Math.max(0, Math.round(s));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  return `${h}h ${m}m`;
};

// "5 min ago" style label for tracker heartbeats.
export const fmtAgo = (d) => {
  if (!d) return '—';
  const secs = Math.max(0, dayjs().diff(dayjs(d), 'second'));
  if (secs < 60) return `${secs}s ago`;
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

// Colour band for an activity percentage (used by tracker timelines & bars).
export const activityColor = (pct) => {
  const p = Number(pct) || 0;
  if (p < 30) return 'bg-rose-500';
  if (p < 60) return 'bg-amber-500';
  return 'bg-emerald-500';
};

// Allotted lunch break; anything beyond this is flagged as overtime.
export const LUNCH_LIMIT_MINUTES = 60;
export const isLunchOver = (mins) => (mins || 0) > LUNCH_LIMIT_MINUTES;

export const initials = (name = '') =>
  name
    .split(' ')
    .map((s) => s[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();

export const statusColor = {
  pending: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  approved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  rejected: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  cancelled: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  present: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  absent: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
  late: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  'half-day': 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  leave: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300',
  wfh: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  'in-progress': 'bg-sky-100 text-sky-800 dark:bg-sky-900/30 dark:text-sky-300',
  completed: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  'on-hold': 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  active: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
  archived: 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
};

export const priorityColor = {
  low: 'bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200',
  medium: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  high: 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300',
};

const API_BASE = import.meta.env.VITE_API_URL ?? '';

export function download(url, filename, token) {
  const fullUrl = `${API_BASE}/api/${url}`;
  if (token) {
    // Fetch with the auth header, then save the response as a file.
    fetch(fullUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`Download failed (${r.status})`);
        return r.blob();
      })
      .then((blob) => {
        const u = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = u;
        link.download = filename || '';
        link.click();
        URL.revokeObjectURL(u);
      })
      .catch((e) => {
        toast.error(e.message || 'Download failed');
      });
    return;
  }
  const a = document.createElement('a');
  a.href = fullUrl;
  a.download = filename || '';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
